export type PlatformBrandingPublicDto = {
  platformName: string;
  primaryColor: string;
  primaryForeground: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  logoUrlDark: string | null;
  accentColor: string | null;
  supportUrl: string | null;
  privacyPolicyUrl: string | null;
  termsOfServiceUrl: string | null;
};

export const ORYA_PRIMARY_COLOR = '#4A0E69';

export function withOryaTheme(dto: PlatformBrandingPublicDto): PlatformBrandingPublicDto {
  return { ...dto, primaryColor: ORYA_PRIMARY_COLOR, primaryForeground: '#ffffff', accentColor: null };
}

export const DEFAULT_PLATFORM_BRANDING: PlatformBrandingPublicDto = {
  platformName: 'Orya',
  primaryColor: ORYA_PRIMARY_COLOR,
  primaryForeground: '#ffffff',
  logoUrl: null,
  faviconUrl: null,
  logoUrlDark: null,
  accentColor: null,
  supportUrl: null,
  privacyPolicyUrl: null,
  termsOfServiceUrl: null,
};

export function getManagerApiOrigin(): string {
  const base = import.meta.env.VITE_API_URL || 'https://api.oryacrm.com/api/manager';
  return base.replace(/\/api\/manager\/?$/, '');
}

export function getPublicPlatformBrandingUrl(): string {
  return `${getManagerApiOrigin()}/api/public/platform-branding`;
}

/** Simple darken for #RRGGBB hover states. */
export function darkenHex(hex: string, amount: number): string {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return '#0073e6';
  const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function applyPlatformBrandingToDocument(branding: PlatformBrandingPublicDto): void {
  const dto = withOryaTheme(branding);
  const root = document.documentElement;
  root.style.setProperty('--primary', dto.primaryColor);
  const hex = dto.primaryColor.replace('#', '').trim();
  const expanded = hex.length === 3 ? hex.split('').map((part) => part + part).join('') : hex;
  const safeHex = /^[0-9a-f]{6}$/i.test(expanded) ? expanded : '0084ff';
  root.style.setProperty('--primary-rgb', [0, 2, 4].map((offset) => parseInt(safeHex.slice(offset, offset + 2), 16)).join(' '));
  root.style.setProperty('--primary-foreground', dto.primaryForeground);
  root.style.setProperty('--primary-hover', darkenHex(dto.primaryColor, 0.08));
  if (dto.accentColor) {
    root.style.setProperty('--accent', dto.accentColor);
  } else {
    root.style.removeProperty('--accent');
  }

  document.title = `${dto.platformName} Manager`;

  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = dto.faviconUrl || '/favicon.ico';
}
